import { z } from 'zod';
import { MAX_MODEL_SIZE_BYTES } from '../constants.js';
import { modelFormatSchema, modelSourceSchema, modelStatusSchema, modelTaskSchema, } from '../enums.js';
import { libraryCommercialSchema, libraryPlatformSchema } from './model-library.js';
/**
 * Enregistrement d'un modèle dans le registre (ADR-0014, FR-37).
 *
 * L'appel crée la ligne et renvoie une URL pré-signée : l'artefact `.onnx` part ensuite du
 * navigateur droit vers le stockage objet, il **ne transite pas par l'API** (règle d'architecture
 * §4, comme une vidéo). Les noms de classes sont **ordonnés** — l'indice du modèle est la position
 * dans la liste (agnostique au modèle, cf. ADR-0013).
 */
export const registerModelSchema = z.object({
    name: z.string().trim().min(1).max(120),
    version: z.string().trim().min(1).max(60),
    description: z.string().trim().max(2000).optional(),
    format: modelFormatSchema.default('ONNX'),
    task: modelTaskSchema.default('DETECTION'),
    source: modelSourceSchema.default('IMPORTED'),
    classNames: z.array(z.string().trim().min(1).max(120)).max(10000).default([]),
    inputSize: z.number().int().positive().max(8192).default(640),
    filename: z.string().trim().min(1).max(255),
    sizeBytes: z.number().int().positive().max(MAX_MODEL_SIZE_BYTES),
    contentType: z.string().trim().max(255).default('application/octet-stream'),
});
/** Mise à jour des métadonnées d'un modèle (FR-37). L'artefact, lui, ne se réécrit pas. */
export const updateModelSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    version: z.string().trim().min(1).max(60).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    classNames: z.array(z.string().trim().min(1).max(120)).max(10000).optional(),
    inputSize: z.number().int().positive().max(8192).optional(),
});
/**
 * Provenance d'un modèle **adopté** depuis la bibliothèque (ADR-0019). La licence est **figée au
 * moment de l'adoption** (les CGU/licences évoluent) pour rester la référence sous laquelle le poids
 * a été pris. `weightSha256` est nul tant que le worker n'a pas téléchargé et vérifié l'octet.
 */
export const modelProvenanceSchema = z.object({
    libraryItemId: z.string(),
    platform: libraryPlatformSchema,
    repoUrl: z.string().url(),
    licenseSpdx: z.string(),
    licenseCommercial: libraryCommercialSchema,
    militaryAllowed: z.boolean(),
    weightSha256: z.string().nullable(),
});
export const modelSchema = z.object({
    id: z.string(),
    organizationId: z.string(),
    name: z.string(),
    version: z.string(),
    description: z.string().nullable(),
    format: modelFormatSchema,
    task: modelTaskSchema,
    source: modelSourceSchema,
    status: modelStatusSchema,
    classNames: z.array(z.string()),
    inputSize: z.number().int().positive(),
    filename: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    /** Résumé de la dernière évaluation (rempli en Phase A) ; `null` tant qu'aucune n'a eu lieu. */
    metrics: z.record(z.unknown()).nullable(),
    /** Renseignée uniquement pour un modèle adopté (`source: 'ADOPTED'`), nulle sinon (ADR-0019). */
    provenance: modelProvenanceSchema.nullable(),
    createdById: z.string().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/**
 * Adoption d'un modèle de la bibliothèque (ADR-0019) : on désigne l'item curé, le serveur vérifie
 * l'éligibilité (ONNX + licence), crée un modèle **cloisonné** `PENDING` et enfile le téléchargement
 * du poids. Idempotent : ré-adopter le même item renvoie le modèle existant.
 */
export const adoptModelSchema = z.object({
    libraryItemId: z.string().trim().min(1).max(120),
});
/** Réponse à l'enregistrement : le modèle créé (statut `PENDING`) + l'URL de dépôt de l'artefact. */
export const modelUploadSchema = z.object({
    model: modelSchema,
    uploadUrl: z.string().url(),
});
//# sourceMappingURL=model.js.map